import { Dispatch, SetStateAction, useEffect, useState } from "react"

// ** Internal Elements
import { Label } from "@pma/components/common/label"
import { Checkbox } from "@pma/components/common/checkbox"
import { Separator } from "@pma/components/common/separator"
import { Avatar, AvatarImage } from "@pma/components/common/avatar"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@pma/components/common/dialog"

// ** Types
import { DictType } from "@pma/types"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** helper
import { cn } from "@pma/utils/helpers"

// ** Custom Hooks
import useTranslator from "@pma/hooks/use-translator"
import { useMutation } from "@tanstack/react-query"
import { editTaskService } from "@pma/services/task"
import { toast } from "@pma/hooks/use-toast"

interface TeamMembersDialogProps {
  taskDetails?: any
  refetchTaskList?: () => void
  selectedTeamMembers: any[]
  teamMembersDialog: boolean
  setSelectedTeamMembers: Dispatch<SetStateAction<any[]>>
  setTeamMembersDialog: Dispatch<SetStateAction<boolean>>
}

const TeamMembersDialog = ({
  taskDetails,
  refetchTaskList,
  teamMembersDialog,
  selectedTeamMembers,
  setTeamMembersDialog,
  setSelectedTeamMembers,
}: TeamMembersDialogProps) => {
  const { authConfig } = useAuth()
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"

  const organizationUsers = authConfig?.organizationUsers || []

  const [searchText, setSearchText] = useState<string>("")
  const [selectedUser, setSelectedUser] = useState<any[]>([])
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set([]))
  const [availableUsersArr, setAvailableUserArr] = useState(organizationUsers)

  useEffect(() => {
    handleSetStateValue()
  }, [])

  const handleSetStateValue = () => {
    // Filter User List depend on User Name is not empty
    const filterUserList = organizationUsers.filter((user: DictType) => user.name)

    // Find the User Already selected
    const newUserList = filterUserList.filter((user: DictType) =>
      selectedTeamMembers.some((userDetails: DictType) => userDetails.id === user.id),
    )

    // Set value in State
    if (newUserList.length) {
      for (let i = 0; i < newUserList.length; i++) {
        handleCheckboxChange(newUserList[i])
      }
    }

    setAvailableUserArr(filterUserList)
  }

  const handleCheckboxChange = (user: any) => {
    setSelectedRows((prev) => {
      const newSelected = new Set(prev)
      if (newSelected.has(user?.id)) newSelected.delete(user?.id)
      else newSelected.add(user?.id)

      return newSelected
    })

    const userExisted = selectedUser.some((userDetails) => userDetails.id === user.id)
    if (!userExisted) setSelectedUser((pre) => [...pre, user])
    else {
      setSelectedUser((pre: any) => {
        const filterList = pre.filter((userDetails: any) => userDetails.id !== user.id)
        return filterList
      })
    }
  }

  const handleSubmit = () => {
    setSelectedTeamMembers(selectedUser)
    setTeamMembersDialog(false)
    if(taskDetails) {
      const idArray = selectedUser.map((user) => user.id)
      updateTaskAssignedMutation.mutate(idArray)
    }
  }

  const updateTaskAssignedMutation = useMutation({
    mutationKey: ["update-task-assign-to-user"],
    mutationFn: (users: number[]) =>
      editTaskService({
        responsible_id: users,
        inform_users: true,
        task_id: taskDetails?.id,
      }),
    onSuccess: () => {
      refetchTaskList?.()
      toast({
        title: translate("Success"),
        description: translate("Task Assigned updated successfully"),
        variant: "success",
      })
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Error occurred"),
        variant: "error",
      })
    },
  })

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const filtered = organizationUsers.filter((user: DictType) =>
        user.name.toLowerCase().includes(searchText.toLowerCase()),
      )
      setAvailableUserArr(filtered)
    }, 300) // Debounce delay in ms

    return () => clearTimeout(delayDebounce) // Cleanup timeout
  }, [searchText])

  return (
    <Dialog open={teamMembersDialog} onOpenChange={setTeamMembersDialog}>
      <DialogContent>
        <DialogHeader className='flex items-center justify-between border-b p-6'>
          <DialogTitle>{translate("Invite Team Members")}</DialogTitle>
        </DialogHeader>
        <div>
          <input
            className={cn(
              "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500",
              isHebrew ? "rtl" : "ltr",
            )}
            placeholder={translate("Search User")}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <Separator className='my-2' />
          <div className={cn("h-96 overflow-auto", isHebrew ? "rtl" : "ltr")}>
            {availableUsersArr.length > 0 ? (
              availableUsersArr.map((user: DictType, index: number) => {
                return (
                  <div key={user?.id}>
                    <div
                      key={user.value}
                      className='my-1 flex cursor-pointer flex-row items-center gap-2 rounded-md p-1 hover:bg-indigo-200'
                    >
                      <Checkbox
                        id={`user_${user?.id}`}
                        checked={selectedRows.has(user.id)}
                        onCheckedChange={() => handleCheckboxChange(user)}
                      />
                      <Label
                        htmlFor={`user_${user?.id}`}
                        className='flex w-full cursor-pointer items-center justify-start gap-2'
                      >
                        <Avatar className='h-7 w-7'>
                          <AvatarImage
                            src={
                              user?.user_picture?.url ||
                              `${process.env.PUBLIC_URL}/user-profile.svg`
                            }
                          />
                        </Avatar>{" "}
                        {user?.name}
                      </Label>
                    </div>
                    {index != availableUsersArr.length - 1 ? (
                      <Separator className='my-0.5' />
                    ) : null}
                  </div>
                )
              })
            ) : (
              <p>{translate("No Users")}</p>
            )}
          </div>
        </div>
        <DialogFooter className={cn("border-t pt-4", isHebrew ? "!space-x-reverse" : "")}>
          <button
            type='button'
            className='rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:border-gray-400 hover:bg-gray-50'
            onClick={() => setTeamMembersDialog(false)}
          >
            {translate("Cancel")}
          </button>
          <button
            type='button'
            className='rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-600'
            onClick={handleSubmit}
          >
            {translate("Save")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default TeamMembersDialog
